/**
 * ReminderSettings — standalone notification preferences panel.
 * Used inside Library /library/reminders. No SectionLayout wrapper —
 * Library.tsx provides its own back-button header.
 */

import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { useAuth } from '../context/AuthContext';
import { db } from '../lib/firebase';

type NotificationSetting = 'all' | 'logs' | 'meds' | 'none';

function saveLocalSettings(updates: Record<string, unknown>) {
  const current = JSON.parse(localStorage.getItem('koda_settings') || '{}');
  localStorage.setItem('koda_settings', JSON.stringify({ ...current, ...updates }));
  window.dispatchEvent(new Event('storage'));
}

function TimeInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [hours = '08', minutes = '00'] = value.split(':');

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <select
          value={hours}
          onChange={e => onChange(`${e.target.value}:${minutes}`)}
          className="text-sm font-semibold text-text-secondary bg-white px-2 py-2 outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
        >
          {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(hour => (
            <option key={hour} value={hour}>{hour}</option>
          ))}
        </select>
        <span className="text-sm font-bold text-gray-400 px-0.5">:</span>
        <select
          value={minutes}
          onChange={e => onChange(`${hours}:${e.target.value}`)}
          className="text-sm font-semibold text-text-secondary bg-white px-2 py-2 outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
        >
          {['00', '15', '30', '45'].map(min => (
            <option key={min} value={min}>{min}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SettingsRow({ label, children, align = 'center' }: { label: string; children: React.ReactNode; align?: 'center' | 'start' }) {
  return (
    <div className={cn('flex gap-4 py-4 border-b border-gray-50 last:border-0', align === 'start' ? 'items-start' : 'items-center')}>
      <span className="text-sm text-text-secondary w-40 shrink-0">{label}</span>
      <div className="flex-1 flex flex-wrap items-center justify-end gap-2">{children}</div>
    </div>
  );
}

export function ReminderSettings() {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [notificationSetting, setNotificationSetting] = useState<NotificationSetting>('all');
  const [reminderTime, setReminderTime] = useState('08:00');
  const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('koda_settings');
      if (!saved) return;
      const settings = JSON.parse(saved);
      if (settings.notificationSetting) setNotificationSetting(settings.notificationSetting as NotificationSetting);
      if (settings.logReminderAM) setReminderTime(settings.logReminderAM);
    } catch {
      // ignore malformed settings
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const syncFromCloud = async () => {
      try {
        const prefSnap = await getDoc(doc(db, 'users', user.id, 'notification_preferences', 'default'));
        if (!prefSnap.exists() || cancelled) return;

        const prefs = prefSnap.data() as {
          notification_setting?: NotificationSetting;
          log_reminder_am?: string;
        };

        const updates: Record<string, unknown> = {};

        if (prefs.notification_setting && ['all', 'logs', 'meds', 'none'].includes(prefs.notification_setting)) {
          setNotificationSetting(prefs.notification_setting);
          updates.notificationSetting = prefs.notification_setting;
        }
        if (prefs.log_reminder_am) {
          setReminderTime(prefs.log_reminder_am);
          updates.logReminderAM = prefs.log_reminder_am;
        }

        if (Object.keys(updates).length > 0) {
          saveLocalSettings(updates);
        }
      } catch {
        // ignore cloud sync failures
      }
    };

    void syncFromCloud();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const check = () => setPermissionState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
    check();
    const interval = setInterval(check, 1500);
    return () => clearInterval(interval);
  }, []);

  const saveSetting = async (updates: Record<string, unknown>, permissionOverride?: NotificationPermission | 'unsupported') => {
    saveLocalSettings(updates);

    if (!user?.id) return;

    const notificationSettingFromState = (updates.notificationSetting as NotificationSetting | undefined) ?? notificationSetting;
    const logReminderFromState = (updates.logReminderAM as string | undefined) ?? reminderTime;

    try {
      const permission = permissionOverride ?? (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
      const backgroundPushEnabled =
        permission === 'granted' && notificationSettingFromState !== 'none';

      await setDoc(doc(db, 'users', user.id, 'notification_preferences', 'default'), {
        user_id: user.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        notification_setting: notificationSettingFromState,
        meds_enabled: notificationSettingFromState === 'all' || notificationSettingFromState === 'meds',
        logs_enabled: notificationSettingFromState === 'all' || notificationSettingFromState === 'logs',
        log_reminder_am: logReminderFromState,
        permission,
        backgroundPushEnabled,
        updated_at: new Date().toISOString(),
      }, { merge: true });
    } catch {
      // ignore write failures
    }
  };

  const requestPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setPermissionState(result);
    await saveSetting({}, result);
  };

  const handleNotificationSettingChange = async (next: NotificationSetting) => {
    setNotificationSetting(next);
    const shouldRequestPermission = next !== 'none' && typeof Notification !== 'undefined' && Notification.permission === 'default';
    if (shouldRequestPermission) {
      await requestPermission();
      await saveSetting({ notificationSetting: next }, typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
      return;
    }
    void saveSetting({ notificationSetting: next });
  };

  const appEnabled = notificationSetting !== 'none';
  const browserGranted = permissionState === 'granted';

  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-100 rounded-3xl p-7">
        <SettingsRow label={t('options.permissions')} align="start">
          <div className="flex flex-col items-end gap-2 w-full">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold', appEnabled ? 'bg-primary-brown/10 text-primary-brown' : 'bg-gray-100 text-text-secondary')}>
                {appEnabled ? t('options.appOn') : t('options.appOff')}
              </span>
              <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold', browserGranted ? 'bg-primary-brown/10 text-primary-brown' : permissionState === 'denied' ? 'bg-error-red/5 text-error-red' : 'bg-gray-100 text-text-secondary')}>
                {browserGranted ? t('options.browserAllowed') : (permissionState === 'unsupported' ? t('options.browserUnsupported') : permissionState === 'denied' ? t('options.browserBlocked') : t('options.browserNotAsked'))}
              </span>
            </div>
            {permissionState === 'default' && (
              <button
                onClick={requestPermission}
                className="self-start px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-brown text-white hover:bg-primary-brown/80 transition-colors"
              >
                {t('options.permissionGrant')}
              </button>
            )}
            {permissionState === 'denied' && (
              <p className="text-xs text-error-red leading-relaxed">{t('options.browserBlockedHelp')}</p>
            )}
          </div>
        </SettingsRow>

        <SettingsRow label={t('options.activate')}>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {([
              { key: 'all', label: t('options.notificationsAll') },
              { key: 'logs', label: t('options.notificationsLogs') },
              { key: 'meds', label: t('options.notificationsMeds') },
              { key: 'none', label: t('options.notificationsNone') },
            ] as const).map(opt => (
              <button
                key={opt.key}
                onClick={() => { void handleNotificationSettingChange(opt.key); }}
                className={cn('px-3 py-1 rounded-md text-xs font-medium transition-all', notificationSetting === opt.key ? 'bg-white shadow-sm text-primary-brown' : 'text-text-secondary')}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </SettingsRow>

        {(notificationSetting === 'all' || notificationSetting === 'logs') && (
          <SettingsRow label={t('options.logReminderTime')} align="start">
            <div className="pt-0.5">
              <TimeInput
                label={t('options.logReminderTime')}
                value={reminderTime}
                onChange={value => {
                  setReminderTime(value);
                  void saveSetting({ logReminderAM: value });
                }}
              />
            </div>
          </SettingsRow>
        )}
      </section>

    </div>
  );
}
